import React from "react";
import { Database, CheckCircle2, AlertTriangle, Loader2, PlayCircle, HardDrive } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "../lib/api";

function formatBytes(bytes) {
  if (!bytes) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
}

export default function BackupStatusPanel() {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [running, setRunning] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminApi().get("/admin/backup/status");
      setData(r.data);
    } catch (e) {
      toast.error("Kunne ikke hente backup-status");
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const runNow = async () => {
    if (!window.confirm("Kør en backup nu?\n\nDet tager normalt 5-20 sekunder og påvirker ikke drift.")) return;
    setRunning(true);
    try {
      const { data: result } = await adminApi().post("/admin/backup/run-now");
      if (result.status === "success") {
        toast.success(`Backup OK · ${formatBytes(result.size_bytes)} · ${result.duration_s}s`);
      } else {
        toast.error(`Backup fejlede: ${result.error || "ukendt fejl"}`);
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke starte backup");
    } finally {
      setRunning(false);
    }
  };

  if (loading || !data) {
    return <div className="bg-white border border-brand p-5 text-sm text-brand-text2" data-testid="backup-loading">Indlæser backup-status…</div>;
  }

  const lastOK = data.last_success_at;
  const lastFail = data.last_failure_at;
  const isHealthy = lastOK && (!lastFail || new Date(lastOK) > new Date(lastFail));

  return (
    <div className="space-y-4" data-testid="backup-panel">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display font-bold text-2xl text-brand-dark flex items-center gap-2">
            <Database size={22} className="text-brand-red" />
            Database-backup
          </h2>
          <p className="text-sm text-brand-text2 mt-1">
            {data.schedule} · {data.retention_days} dages historik
          </p>
        </div>
        <button
          onClick={runNow}
          disabled={running}
          className="text-sm bg-brand-dark text-white hover:bg-brand-red px-4 py-2 flex items-center gap-2 disabled:opacity-60 transition-colors"
          data-testid="backup-run-now"
        >
          {running ? <><Loader2 size={14} className="animate-spin" /> Kører…</> : <><PlayCircle size={14} /> Kør backup nu</>}
        </button>
      </div>

      {/* Health card */}
      <div className={`border p-5 ${isHealthy ? "bg-emerald-50 border-emerald-300" : "bg-amber-50 border-amber-300"}`}>
        <div className="flex items-center gap-3 mb-2">
          {isHealthy ? (
            <CheckCircle2 size={20} className="text-emerald-700" />
          ) : (
            <AlertTriangle size={20} className="text-amber-700" />
          )}
          <span className={`font-display font-bold text-sm ${isHealthy ? "text-emerald-900" : "text-amber-900"}`}>
            {isHealthy ? "Backup virker normalt" : (lastOK ? "Seneste backup fejlede" : "Ingen succesfuld backup endnu")}
          </span>
        </div>
        <div className="grid grid-cols-2 gap-4 text-xs text-brand-text2 mt-3">
          <div>
            <div className="kicker mb-1">Senest succes</div>
            <div className="font-mono">{lastOK ? lastOK.slice(0, 19).replace("T", " ") : "—"}</div>
          </div>
          <div>
            <div className="kicker mb-1">Senest fejl</div>
            <div className="font-mono">{lastFail ? lastFail.slice(0, 19).replace("T", " ") : "—"}</div>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid sm:grid-cols-3 gap-3">
        <div className="bg-white border border-brand p-4">
          <div className="kicker mb-1">Snapshots på disk</div>
          <div className="text-2xl font-display font-bold text-brand-dark">{data.snapshot_files?.length || 0}</div>
        </div>
        <div className="bg-white border border-brand p-4">
          <div className="kicker mb-1">Samlet diskforbrug</div>
          <div className="text-2xl font-display font-bold text-brand-dark">{formatBytes(data.total_archive_size_bytes)}</div>
        </div>
        <div className="bg-white border border-brand p-4">
          <div className="kicker mb-1">Log-poster</div>
          <div className="text-2xl font-display font-bold text-brand-dark">{data.log_entries?.length || 0}</div>
        </div>
      </div>

      {/* Recent log */}
      <div className="bg-white border border-brand">
        <div className="px-5 py-3 border-b border-brand bg-brand-bg2">
          <h3 className="font-display font-bold text-sm text-brand-dark">Seneste backup-kørsler</h3>
        </div>
        {(!data.log_entries || data.log_entries.length === 0) ? (
          <div className="p-5 text-sm text-brand-text2">Ingen log-poster endnu. Klik "Kør backup nu" for at lave den første.</div>
        ) : (
          <div className="divide-y divide-brand">
            {data.log_entries.slice(0, 15).map((log) => (
              <div key={log.id} className="px-5 py-3 flex items-center justify-between gap-3" data-testid={`backup-log-${log.id}`}>
                <div className="flex items-center gap-3 min-w-0">
                  {log.status === "success" ? (
                    <CheckCircle2 size={16} className="text-emerald-600 flex-shrink-0" />
                  ) : (
                    <AlertTriangle size={16} className="text-red-600 flex-shrink-0" />
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-mono text-brand-dark">{log.started_at?.slice(0, 19).replace("T", " ")}</div>
                    {log.error && <div className="text-xs text-red-700 mt-0.5 truncate">Fejl: {log.error}</div>}
                  </div>
                </div>
                <div className="flex items-center gap-4 text-xs text-brand-text2 flex-shrink-0">
                  <span className="font-mono">{formatBytes(log.size_bytes)}</span>
                  <span className="font-mono">{log.duration_s ? `${log.duration_s}s` : "—"}</span>
                  {log.file_exists ? (
                    <span className="text-emerald-700 flex items-center gap-1"><HardDrive size={11} /> på disk</span>
                  ) : (
                    <span className="text-slate-400">slettet</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <details className="bg-brand-bg2 border border-brand p-4 text-sm">
        <summary className="cursor-pointer font-display font-bold text-brand-dark">Sådan gendannes en backup (restore-instruktion)</summary>
        <div className="mt-3 space-y-2 text-brand-text2">
          <p>Backup-filer ligger i <code className="text-brand-red">/app/backups/</code> som <code>auto_YYYYMMDD_HHMMSS.tar.gz</code>.</p>
          <p>For at gendanne en backup (kører som root på serveren):</p>
          <pre className="bg-brand-dark text-white p-3 text-xs overflow-x-auto">
{`# 1. Pak arkivet ud
cd /tmp && tar xzf /app/backups/auto_<TIDSSTEMPEL>.tar.gz

# 2. Stop backend så ingen skriver til DB under restore
sudo supervisorctl stop backend

# 3. Drop nuværende DB og restore (PAS PÅ! Overskriver alt)
mongorestore --uri="mongodb://localhost:27017" \\
  --db=test_database \\
  --drop \\
  /tmp/snapshot_<TIDSSTEMPEL>/test_database/

# 4. Start backend igen
sudo supervisorctl start backend`}
          </pre>
          <p className="text-xs">
            <b>⚠️ Vigtigt:</b> Restore overskriver hele databasen. Tag en frisk backup FØR du restorer, hvis nuværende data har værdi.
            Kontakt udvikleren før første restore i produktion.
          </p>
        </div>
      </details>
    </div>
  );
}
